import z from "zod";

const quickbooksErrorTypeValues = [
  "CONNECTION_NOT_FOUND",
  "TOKEN_NOT_FOUND",
  "INVOICE_NUMBER_TOO_LONG",
  "INVOICE_CREATION_FAILED",
  "PAYMENT_NOT_FOUND",
  "PAYMENT_TXNDATE_MISSING",
  "PAYMENT_TRANSACTION_MISMATCH",
  "CUSTOMER_CREATION_FAILED",
  "ACCOUNT_TYPE_NOT_SUPPORTED",
  "FASTPAY_ACCOUNT_MISSING",
  "VENDOR_NAME_COLLISION",
  "UNBALANCED_CREDIT_APPLICATION",
] as const;
const quickbooksErrorTypeSchema = z.enum(quickbooksErrorTypeValues);
const QuickbooksErrorType = quickbooksErrorTypeSchema.enum;
type QuickbooksErrorType = z.infer<typeof quickbooksErrorTypeSchema>;

export type QuickbooksErrors =
  | { errorType: typeof QuickbooksErrorType.CONNECTION_NOT_FOUND; data: { remiEntity?: string; companyId?: string } }
  | { errorType: typeof QuickbooksErrorType.TOKEN_NOT_FOUND; data: { companyId: string } }
  | {
      errorType: typeof QuickbooksErrorType.INVOICE_NUMBER_TOO_LONG;
      data: { invoiceNumber: string; maxLength: number };
    }
  | {
      errorType: typeof QuickbooksErrorType.INVOICE_CREATION_FAILED;
      data: { customerId: string; customerMemo?: string };
    }
  | { errorType: typeof QuickbooksErrorType.PAYMENT_NOT_FOUND; data: { invoiceId: string } }
  | { errorType: typeof QuickbooksErrorType.PAYMENT_TXNDATE_MISSING; data: { paymentId: string } }
  | {
      errorType: typeof QuickbooksErrorType.PAYMENT_TRANSACTION_MISMATCH;
      data: { invoiceId: string; linkedTransactions: number; paymentCount: number };
    }
  | {
      errorType: typeof QuickbooksErrorType.CUSTOMER_CREATION_FAILED;
      data: { displayName: string; organizationId: string; internalId?: string };
    }
  | { errorType: typeof QuickbooksErrorType.ACCOUNT_TYPE_NOT_SUPPORTED; data: { billType?: string } }
  | {
      errorType: typeof QuickbooksErrorType.FASTPAY_ACCOUNT_MISSING;
      data: { fastpayDeductionAmount: number; customerId: string; description: string };
    }
  | {
      errorType: typeof QuickbooksErrorType.VENDOR_NAME_COLLISION;
      data: { name: string; displayName: string; organizationId: string; remiEntity: string };
    }
  | {
      errorType: typeof QuickbooksErrorType.UNBALANCED_CREDIT_APPLICATION;
      data: { creditExternalId: string; billLineTotal: number; creditLineTotal: number };
    };

export const quickbooksErrorDetails: Record<QuickbooksErrorType, { sentryDescription: string }> = {
  CONNECTION_NOT_FOUND: {
    sentryDescription: "QuickBooks connection configuration not found for the specified entity or company ID",
  },
  TOKEN_NOT_FOUND: { sentryDescription: "QuickBooks refresh token not found in database for company connection" },
  INVOICE_NUMBER_TOO_LONG: { sentryDescription: "Invoice number exceeds QuickBooks maximum length limit" },
  INVOICE_CREATION_FAILED: { sentryDescription: "Failed to create invoice in QuickBooks" },
  PAYMENT_NOT_FOUND: { sentryDescription: "No payments found for the specified invoice" },
  PAYMENT_TXNDATE_MISSING: { sentryDescription: "Transaction date is required for payment transfer but is missing" },
  PAYMENT_TRANSACTION_MISMATCH: { sentryDescription: "Invoice has linked transactions that are not all payments" },
  CUSTOMER_CREATION_FAILED: { sentryDescription: "Failed to create customer and no existing customer found" },
  ACCOUNT_TYPE_NOT_SUPPORTED: { sentryDescription: "Accrued account type not supported for the specified bill type" },
  FASTPAY_ACCOUNT_MISSING: { sentryDescription: "Fastpay account ID is required for fastpay deductions" },
  VENDOR_NAME_COLLISION: {
    sentryDescription: "QuickBooks rejected vendor DisplayName as duplicate despite orgId-based disambiguation",
  },
  UNBALANCED_CREDIT_APPLICATION: {
    sentryDescription: "Credit-only BillPayment lines do not balance (Σ bill ≠ Σ credit) — would record cash movement",
  },
};
